import { injectable } from 'inversify';
import type {
  CreateArticlePayload,
  CreateCategoryPayload,
  HelpArticleBundleDTO,
  HelpArticleDTO,
  HelpCategoryDTO,
  HelpLayoutDTO,
  IHelpDocumentationRepository,
  ListArticlesParams,
  SaveHelpLayoutPayload,
  UpdateArticlePayload,
  UpdateCategoryPayload,
  UpsertArticleBundlePayload,
} from '@/domain/repositories/IHelpDocumentationRepository';
import { httpClient } from '@/infrastructure/http/client/httpClient';

@injectable()
export class ApiHelpDocumentationRepository implements IHelpDocumentationRepository {
  async listCategories(): Promise<HelpCategoryDTO[]> {
    const response = await httpClient.get<HelpCategoryDTO[]>('/help/categories');
    return response.data;
  }

  async createCategory(data: CreateCategoryPayload): Promise<HelpCategoryDTO> {
    const response = await httpClient.post<HelpCategoryDTO>('/help/categories', data);
    return response.data;
  }

  async updateCategory(id: string, data: UpdateCategoryPayload): Promise<HelpCategoryDTO> {
    const response = await httpClient.patch<HelpCategoryDTO>(
      `/help/categories/${encodeURIComponent(id)}`,
      data
    );
    return response.data;
  }

  async deleteCategory(id: string): Promise<{ deleted: 'soft' | 'hard' }> {
    const response = await httpClient.delete<{ deleted: 'soft' | 'hard' }>(
      `/help/categories/${encodeURIComponent(id)}`
    );
    return response.data;
  }

  async listArticles(params: ListArticlesParams = {}): Promise<HelpArticleDTO[]> {
    const query = new URLSearchParams();
    if (params.locale) query.set('locale', params.locale);
    if (params.contentType) query.set('contentType', params.contentType);
    if (params.categoryId) query.set('categoryId', params.categoryId);
    if (params.published === true) query.set('published', 'true');
    if (params.published === false) query.set('published', 'false');
    const qs = query.toString();
    const response = await httpClient.get<HelpArticleDTO[]>(`/help/articles${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getArticle(id: string): Promise<HelpArticleDTO> {
    const response = await httpClient.get<HelpArticleDTO>(
      `/help/articles/${encodeURIComponent(id)}`
    );
    return response.data;
  }

  async getBundleById(id: string): Promise<HelpArticleBundleDTO> {
    const response = await httpClient.get<HelpArticleBundleDTO>(
      `/help/articles/${encodeURIComponent(id)}/bundle`
    );
    return response.data;
  }

  async getBundleBySlug(slug: string): Promise<HelpArticleBundleDTO> {
    const response = await httpClient.get<HelpArticleBundleDTO>(
      `/help/articles/by-slug/${encodeURIComponent(slug)}`
    );
    return response.data;
  }

  async upsertBundle(data: UpsertArticleBundlePayload): Promise<HelpArticleBundleDTO> {
    const response = await httpClient.put<HelpArticleBundleDTO>('/help/articles/bundle', data);
    return response.data;
  }

  async translateText(text: string, from: string, to: string): Promise<string> {
    const response = await httpClient.post<{ translated: string }>('/help/translate', {
      text,
      from,
      to,
    });
    return response.data.translated;
  }

  async createArticle(data: CreateArticlePayload): Promise<HelpArticleDTO> {
    const response = await httpClient.post<HelpArticleDTO>('/help/articles', data);
    return response.data;
  }

  async updateArticle(id: string, data: UpdateArticlePayload): Promise<HelpArticleDTO> {
    const response = await httpClient.patch<HelpArticleDTO>(
      `/help/articles/${encodeURIComponent(id)}`,
      data
    );
    return response.data;
  }

  async deleteArticle(id: string): Promise<void> {
    await httpClient.delete(`/help/articles/${encodeURIComponent(id)}`);
  }

  async getLayout(): Promise<HelpLayoutDTO> {
    const response = await httpClient.get<HelpLayoutDTO>('/help/layout');
    return response.data;
  }

  async saveLayout(data: SaveHelpLayoutPayload): Promise<HelpLayoutDTO> {
    const response = await httpClient.put<HelpLayoutDTO>('/help/layout', data);
    return response.data;
  }

  async updateArticleSortBySlug(slug: string, sortOrder: number): Promise<void> {
    await httpClient.patch(`/help/articles/by-slug/${encodeURIComponent(slug)}/sort`, {
      sortOrder,
    });
  }
}
